import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { clientesApi } from '../api/notasVenta'
import Boton from './Boton'
import { useStoredState } from '../hooks/useStoredState'
import { colors, text, space, sx, radius } from '../design/tokens'
import { fmtFecha, fmtFechaHora, fmtMonto } from '../utils/fecha'

/*
 * Historial del cliente orientado a CRM (Martin, 31 oct 2026): todo lo que
 * pasó con el cliente para evaluar su cuenta — NV, entregas, cobros con
 * problema, reseñas, encuestas, casos, WhatsApp, mails, campañas… La regla
 * vive en el backend (ClienteHistorialService); acá solo se pinta.
 * Vive en CLIENTES → Historia de clientes, solapa "Historial (CRM)".
 */

const GRUPOS = [
  { key: 'ventas', label: 'Ventas' },
  { key: 'entregas', label: 'Entregas' },
  { key: 'cobros', label: 'Cobros' },
  { key: 'opiniones', label: 'Opiniones' },
  { key: 'problemas', label: 'Casos y NC' },
  { key: 'mensajes', label: 'WhatsApp' },
  { key: 'otros', label: 'Gift cards y Wine Club' },
  { key: 'mails', label: 'Mails' },
  { key: 'campanas', label: 'Campañas' },
]
// Mails y campañas son muchos por pedido: arrancan apagados.
const GRUPOS_DEFAULT = GRUPOS.map(g => g.key).filter(k => !['mails', 'campanas'].includes(k))

const ICONO = {
  nv: '🧾', presupuesto: '📝', carrito: '🛒', entrega: '📦', cobro: '💳', resena: '⭐', encuesta: '⭐',
  caso: '⚠️', nc: '↩', whatsapp: '💬', mail: '✉️', campana: '📣', gift_card: '🎁', wine_club: '🍷',
}

const TONO = {
  ok: { background: colors.okBg, color: colors.ok },
  warn: { background: colors.warnBg, color: colors.warn },
  err: { background: colors.errBg, color: colors.err },
  info: { background: colors.infoBg, color: colors.info },
  neutral: { background: colors.bgSoft, color: colors.textSec },
}

const POR_TANDA = 50

export default function HistorialCrm({ cliente, desde, hasta, consulta }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [grupos, setGrupos] = useStoredState('lgr.filtros.historialCrm', GRUPOS_DEFAULT)
  const [mostrar, setMostrar] = useState(POR_TANDA)

  useEffect(() => {
    if (!cliente?.id) return
    let vigente = true
    const cargar = async () => {
      setLoading(true); setError('')
      try {
        const { data: d } = await clientesApi.historialCrm(cliente.id, desde, hasta)
        if (vigente) { setData(d); setMostrar(POR_TANDA) }
      } catch (e) {
        if (vigente) setError(e.response?.data?.error || e.response?.data?.message || 'No se pudo armar el historial.')
      } finally { if (vigente) setLoading(false) }
    }
    cargar()
    return () => { vigente = false }
    // Recarga al cambiar de cliente o al apretar Consultar (consulta++), no con cada tecla de la fecha.
  }, [cliente?.id, consulta]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!cliente) return null
  if (loading && !data) return <div style={{ ...sx.card, marginTop: space.xl, padding: space.xl, color: colors.textSec }}>Armando el historial…</div>
  if (error) return <div style={{ ...st.error, marginTop: space.xl }}>{error}</div>
  if (!data) return null

  const c = data.cliente
  const compras = data.compras
  const eventos = data.eventos.filter(e => grupos.includes(e.grupo))
  const toggle = (k) => setGrupos(g => g.includes(k) ? g.filter(x => x !== k) : [...g, k])
  const cuenta = (k) => data.eventos.filter(e => e.grupo === k).length

  return (
    <>
      {/* Cabecera del cliente + chips */}
      <div style={{ ...sx.card, marginTop: space.xl, padding: space.xl }}>
        <div style={st.cabecera}>
          <div>
            <div style={st.nombre}>{c.nombre}</div>
            <div style={st.sub}>
              {c.codigo_presea ? <span style={sx.mono}>Cód. {c.codigo_presea}</span> : 'Sin código Presea'}
              {c.cuit && <> · CUIT <span style={sx.mono}>{c.cuit}</span></>}
              {c.lista_precios && <> · {c.lista_precios}</>}
              {c.cliente_desde && <> · Cliente desde <span style={sx.mono}>{fmtFecha(c.cliente_desde)}</span></>}
            </div>
          </div>
          <div style={st.chips}>
            {c.lista_negra && <span style={{ ...sx.badge, ...TONO.err }} title={c.lista_negra_motivo || ''}>⛔ Lista negra</span>}
            {c.fraude && <span style={{ ...sx.badge, ...TONO.err }}>🚫 Fraude</span>}
            {c.wine_club && <span style={{ ...sx.badge, ...(c.wine_club.estado === 'active' ? TONO.ok : TONO.neutral) }}>🍷 Wine Club {c.wine_club.nivel || ''}{c.wine_club.estado !== 'active' ? ' (no activo)' : ''}</span>}
            {c.cuenta_web && <span style={{ ...sx.badge, ...TONO.info }} title={c.cuenta_web.ultimo_login_at ? `Último ingreso ${fmtFechaHora(c.cuenta_web.ultimo_login_at)}` : 'Nunca ingresó'}>🌐 Cuenta web</span>}
          </div>
        </div>

        <div style={st.kpis}>
          <Kpi mono label="Compras netas" valor={compras ? `$ ${fmtMonto(compras.neto, 0)}` : '—'} sub={compras ? `${compras.cantidad} ${compras.cantidad === 1 ? 'compra' : 'compras'} · Presea` : 'Sin código Presea'} />
          <Kpi mono label="Ticket promedio" valor={compras?.ticket != null ? `$ ${fmtMonto(compras.ticket, 0)}` : '—'} />
          <Kpi label="Última compra" valor={compras?.dias_desde_ultima != null ? (compras.dias_desde_ultima === 0 ? 'hoy' : `hace ${compras.dias_desde_ultima} ${compras.dias_desde_ultima === 1 ? 'día' : 'días'}`) : '—'} sub={compras?.ultima_compra ? fmtFecha(compras.ultima_compra) : null} />
          <Kpi label="Frecuencia" valor={compras?.frecuencia_dias != null ? `cada ${compras.frecuencia_dias} días` : '—'} />
          <Kpi label="Entregas" valor={`${data.entregas.ok} ✓ · ${data.entregas.fallidas} ✕`} />
          <Kpi label="Calificación" valor={data.calificacion ? `★ ${String(data.calificacion.promedio).replace('.', ',')}` : '—'} sub={data.calificacion ? `${data.calificacion.cantidad} ${data.calificacion.cantidad === 1 ? 'opinión' : 'opiniones'}` : null} />
        </div>
      </div>

      <div style={st.grid}>
        {/* Línea de tiempo */}
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden', flex: '1 1 520px', minWidth: 0 }}>
          <div style={st.timelineHead}>
            <span style={st.cardTitulo}>Línea de tiempo <span style={st.meta}>· <span style={sx.mono}>{desde} → {hasta}</span> · {eventos.length} {eventos.length === 1 ? 'evento' : 'eventos'}</span></span>
            <div style={st.filtros}>
              {GRUPOS.map(g => (
                <button key={g.key} type="button" onClick={() => toggle(g.key)}
                  style={{ ...sx.pill, ...(grupos.includes(g.key) ? sx.pillActive : {}) }}>
                  {g.label} <span style={{ ...sx.mono, color: colors.textMeta }}>{cuenta(g.key)}</span>
                </button>
              ))}
            </div>
          </div>
          {eventos.length === 0 ? (
            <div style={st.vacio}>No hay eventos en este período con los filtros elegidos.</div>
          ) : (
            <>
              {eventos.slice(0, mostrar).map((e, i) => (
                <div key={i} style={st.evento}>
                  <div style={st.eventoFecha}>
                    <div style={sx.mono}>{fmtFecha(e.fecha)}</div>
                  </div>
                  <div style={st.eventoIcono}>{ICONO[e.tipo] || '•'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={st.eventoTitulo}>
                      {e.link ? <Link to={e.link} style={st.link}>{e.titulo}</Link> : e.titulo}
                      {e.tono !== 'neutral' && e.tono !== 'info' && <span style={{ ...st.punto, background: TONO[e.tono].color }} />}
                    </div>
                    {e.detalle && <div style={st.eventoDetalle}>{e.detalle}</div>}
                  </div>
                </div>
              ))}
              {eventos.length > mostrar && (
                <div style={{ padding: space.lg, textAlign: 'center' }}>
                  <Boton variante="secondary" sm onClick={() => setMostrar(m => m + POR_TANDA)}>Ver {Math.min(POR_TANDA, eventos.length - mostrar)} más</Boton>
                </div>
              )}
            </>
          )}
        </div>

        {/* Panel lateral */}
        <div style={st.lateral}>
          {data.alertas.length > 0 && (
            <div style={{ ...sx.card, padding: space.lg }}>
              <div style={st.cardTitulo}>Alertas del período</div>
              {data.alertas.map(a => <div key={a} style={st.alerta}>⚠ {a}</div>)}
            </div>
          )}
          {compras && (compras.mix.length > 0 || compras.top.length > 0) && (
            <div style={{ ...sx.card, padding: space.lg }}>
              <div style={st.cardTitulo}>Lo que compra</div>
              {compras.mix.map(m => (
                <div key={m.categoria} style={st.mixFila}>
                  <span>{m.categoria}</span>
                  <span style={sx.mono}>{m.porcentaje} %</span>
                </div>
              ))}
              {compras.top.length > 0 && <div style={{ ...st.meta, marginTop: space.md, marginBottom: space.xs }}>Lo que más se lleva</div>}
              {compras.top.map(t => (
                <div key={t.codigo} style={st.mixFila}>
                  <span style={st.recorte} title={t.descripcion}>{t.descripcion}</span>
                  <span style={sx.mono}>{fmtMonto(t.unidades, 0)} u.</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ ...sx.card, padding: space.lg }}>
            <div style={st.cardTitulo}>Contacto</div>
            <div style={st.contacto}>{c.email || '— sin email'}</div>
            <div style={st.contacto}>{c.telefono || '— sin teléfono'}</div>
            {c.domicilio && <div style={{ ...st.contacto, color: colors.textSec }}>{c.domicilio}</div>}
          </div>
        </div>
      </div>
    </>
  )
}

function Kpi({ label, valor, sub, mono }) {
  return (
    <div style={st.kpi}>
      <div style={st.kpiLbl}>{label}</div>
      <div style={{ ...st.kpiVal, ...(mono ? sx.mono : {}) }}>{valor}</div>
      {sub && <div style={st.kpiSub}>{sub}</div>}
    </div>
  )
}

const st = {
  error: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, fontSize: text.body },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.lg, flexWrap: 'wrap' },
  nombre: { fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: space.xs },
  chips: { display: 'flex', gap: space.sm, flexWrap: 'wrap' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: space.md, marginTop: space.xl },
  kpi: { background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px` },
  kpiLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  kpiVal: { fontSize: text.title, fontWeight: 600, color: colors.text, marginTop: space.xs },
  kpiSub: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  grid: { display: 'flex', flexWrap: 'wrap', gap: space.xl, marginTop: space.xl, alignItems: 'flex-start' },
  timelineHead: { padding: space.lg, borderBottom: `1px solid ${colors.border}` },
  cardTitulo: { ...sx.cardTitle, marginBottom: space.sm },
  meta: { fontSize: text.sec, fontWeight: 400, color: colors.textMeta },
  filtros: { display: 'flex', gap: space.sm, flexWrap: 'wrap', marginTop: space.sm },
  vacio: { padding: space.xl, textAlign: 'center', color: colors.textSec, fontSize: text.body },
  evento: { display: 'flex', gap: space.md, padding: `${space.md}px ${space.lg}px`, borderBottom: `1px solid ${colors.borderSoft}`, alignItems: 'flex-start' },
  eventoFecha: { width: 84, flexShrink: 0, fontSize: text.sec, color: colors.textSec, paddingTop: 1 },
  eventoIcono: { width: 22, flexShrink: 0, textAlign: 'center' },
  eventoTitulo: { fontSize: text.body, fontWeight: 500, color: colors.text, display: 'flex', alignItems: 'center', gap: space.sm },
  eventoDetalle: { fontSize: text.sec, color: colors.textSec, marginTop: 2, overflowWrap: 'anywhere' },
  link: { color: colors.link, textDecoration: 'none' },
  punto: { width: 8, height: 8, borderRadius: radius.pill, flexShrink: 0 },
  lateral: { display: 'flex', flexDirection: 'column', gap: space.lg, flex: '0 1 280px', minWidth: 240 },
  alerta: { fontSize: text.sec, color: colors.warn, padding: `${space.xs}px 0` },
  mixFila: { display: 'flex', justifyContent: 'space-between', gap: space.md, fontSize: text.sec, color: colors.text, padding: `${space.xs}px 0` },
  recorte: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  contacto: { fontSize: text.sec, color: colors.text, padding: `${space.xs / 2}px 0`, overflowWrap: 'anywhere' },
}
